import { useQuery } from "@tanstack/react-query";
import type { ExpenseCategory } from "@tabipla/shared";
import { useGroups } from "./useGroups";
import { apiClient } from "../../infrastructure/api/client";
import { balanceKeys } from "./queryKeys";

interface RecentExpense {
  id: string;
  groupId: string;
  sessionId: string;
  description: string;
  amount: number;
  category: ExpenseCategory;
  paidById: string;
  paidByName: string;
  createdAt: string;
}

interface SessionItem {
  id: string;
  name: string;
}

interface ExpenseItem {
  id: string;
  sessionId: string;
  description: string;
  amount: number;
  category: ExpenseCategory;
  paidById: string;
  paidByName: string;
  createdAt: string;
}

async function fetchSessionsAndExpenses(groupId: string): Promise<RecentExpense[]> {
  try {
    // Get sessions for this group
    const sessionsResponse = await apiClient.get<SessionItem[]>(`/api/groups/${groupId}/sessions`);
    if (!sessionsResponse.success || !sessionsResponse.data) {
      return [];
    }
    const sessions = sessionsResponse.data;

    // Get expenses for each session
    const allExpenses: RecentExpense[] = [];
    for (const session of sessions) {
      try {
        const expensesResponse = await apiClient.get<ExpenseItem[]>(
          `/api/groups/${groupId}/sessions/${session.id}/expenses`
        );

        if (!expensesResponse.success || !expensesResponse.data) {
          continue;
        }

        const expenses = expensesResponse.data;
        allExpenses.push(
          ...expenses.map((e) => ({
            id: e.id,
            groupId,
            sessionId: e.sessionId,
            description: e.description,
            amount: e.amount,
            category: e.category,
            paidById: e.paidById,
            paidByName: e.paidByName,
            createdAt: e.createdAt,
          }))
        );
      } catch {
        // Skip if expenses fetch fails
      }
    }

    return allExpenses;
  } catch {
    return [];
  }
}

export function useRecentExpenses(limit: number = 20) {
  const { data: groups } = useGroups();

  return useQuery({
    queryKey: balanceKeys.recentExpenses(groups?.map((g) => g.id).join(",") ?? ""),
    queryFn: async () => {
      if (!groups || groups.length === 0) {
        return [];
      }

      // Fetch expenses from all groups in parallel
      const expensesPerGroup = await Promise.all(
        groups.map((group) => fetchSessionsAndExpenses(group.id))
      );

      // Flatten and sort by date (newest first)
      const allExpenses = expensesPerGroup.flat();
      allExpenses.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      // Return limited results
      return allExpenses.slice(0, limit);
    },
    enabled: !!groups && groups.length > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
